import { KPI_NEUTRAL, RISK_LEVEL_COLORS } from "@app/theme/tokens";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import GppMaybeIcon from "@mui/icons-material/GppMaybe";
import PaymentsIcon from "@mui/icons-material/Payments";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import {
  Alert,
  Box,
  Button,
  Divider,
  Link,
  Paper,
  Skeleton,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useParams } from "react-router-dom";
import { TimelineChip } from "../components/Chips";
import KpiCard from "../components/KpiCard";
import { PendingValue } from "../components/PendingValue";
import { useSpofCard } from "../hooks/useSpofRegister";
import PageHeader from "../layout/PageHeader";
import { riskValue, sar } from "../utils/format";
import { spofCardHref } from "../utils/links";

function Fact({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {label}
      </Typography>
      <Box sx={{ mt: 0.5, fontWeight: 600 }}>{children}</Box>
    </Box>
  );
}

/** Each criterion reads as the score the mitigation starts from and the one it leaves behind. */
function ScoreShift({
  label,
  initial,
  residual,
}: {
  label: string;
  initial: number | null;
  residual: number | null;
}) {
  const improved = initial !== null && residual !== null && residual < initial;
  return (
    <>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ justifySelf: "end" }}>
        {initial ?? <PendingValue />}
      </Typography>
      <ArrowRightAltIcon fontSize="small" sx={{ color: "text.disabled" }} />
      <Typography
        variant="body2"
        sx={{
          justifySelf: "end",
          fontWeight: 700,
          color: improved ? "success.main" : "text.primary",
        }}
      >
        {residual ?? <PendingValue />}
      </Typography>
    </>
  );
}

export default function MitigationDetailPage() {
  const { plantId, spofId, mitigationId } = useParams<{
    plantId: string;
    spofId: string;
    mitigationId: string;
  }>();
  const spof = useSpofCard(plantId!, spofId!).data;

  if (!spof) {
    return <Skeleton variant="rounded" height={420} />;
  }

  const spofHref = spofCardHref(spof.power_plant_id, spof.spof_id);
  const mitigation = spof.mitigations.find(
    (entry) => entry.mitigation_id === mitigationId,
  );

  return (
    <Box>
      <PageHeader
        title="Mitigation Detail"
        trail={[
          { label: "Fleet overview", to: "/dashboard" },
          {
            label: spof.power_plant_name ?? "Plant",
            to: `/dashboard/plants/${spof.power_plant_id}`,
          },
          { label: spof.spof_id, to: spofHref },
          { label: mitigationId ?? "Mitigation" },
        ]}
        action={
          <Button
            variant="outlined"
            component={RouterLink}
            to={spofHref}
            startIcon={<ArrowBackIcon />}
          >
            Back to SPOF
          </Button>
        }
      />

      {!mitigation ? (
        <Alert severity="warning">
          {mitigationId} is not part of the mitigation plan of {spof.spof_id}.
        </Alert>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
            }}
          >
            <KpiCard
              label="Current Risk"
              value={riskValue(spof.risk_category, spof.final_risk_score)}
              color={RISK_LEVEL_COLORS[spof.risk_category ?? ""] ?? KPI_NEUTRAL}
              icon={GppMaybeIcon}
            />
            <KpiCard
              label="Residual Risk"
              value={riskValue(
                mitigation.residual_final_risk_category,
                mitigation.residual_final_risk_score,
              )}
              color={
                RISK_LEVEL_COLORS[
                  mitigation.residual_final_risk_category ?? ""
                ] ?? KPI_NEUTRAL
              }
              icon={VerifiedUserIcon}
            />
            <KpiCard
              label="Total Cost"
              value={
                mitigation.total_cost_sar === null
                  ? null
                  : sar(mitigation.total_cost_sar)
              }
              color={KPI_NEUTRAL}
              icon={PaymentsIcon}
            />
          </Box>

          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", letterSpacing: 1 }}
            >
              MITIGATION ID
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {mitigation.mitigation_id}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 1 }}>
              {mitigation.mitigation_short ?? <PendingValue />}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1.5 }}>
              Linked SPOF:{" "}
              <Link component={RouterLink} to={spofHref} underline="hover">
                {spof.spof_id}
              </Link>
              {spof.equipment ? ` - ${spof.equipment}` : ""}
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Mitigation Description
            </Typography>
            <Typography variant="body2">
              {mitigation.mitigation_long ?? mitigation.mitigation_short ?? (
                <PendingValue />
              )}
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
              Risk Scores
            </Typography>
            <Box
              sx={{
                maxWidth: 420,
                display: "grid",
                gridTemplateColumns: "1fr auto auto auto",
                alignItems: "center",
                columnGap: 2,
                rowGap: 1.25,
              }}
            >
              <Box />
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", justifySelf: "end" }}
              >
                Initial
              </Typography>
              <Box />
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", justifySelf: "end" }}
              >
                Residual
              </Typography>

              <ScoreShift
                label="Severity"
                initial={spof.severity}
                residual={mitigation.residual_severity}
              />
              <ScoreShift
                label="Likelihood"
                initial={spof.likelihood}
                residual={mitigation.residual_likelihood}
              />
              <ScoreShift
                label="Detectability"
                initial={spof.detection_difficulty}
                residual={mitigation.residual_detection_difficulty}
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: { xs: "1fr", sm: "repeat(4, 1fr)" },
              }}
            >
              <Fact label="Department">{spof.section ?? <PendingValue />}</Fact>
              <Fact label="Implementation Timeline">
                <TimelineChip phase={mitigation.implementation_phase} />
              </Fact>
              <Fact label="CAPEX">
                {mitigation.estimated_gbl_capex_sar === null ? (
                  <PendingValue />
                ) : (
                  sar(mitigation.estimated_gbl_capex_sar)
                )}
              </Fact>
              <Fact label="OPEX">
                {mitigation.estimated_se_opex_sar === null ? (
                  <PendingValue />
                ) : (
                  sar(mitigation.estimated_se_opex_sar, true)
                )}
              </Fact>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
}
